import { useState, useEffect, useMemo, useCallback } from "react";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Textarea } from "../components/ui/textarea";
import { PhoneInput } from "../components/ui/phone-input";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { divIcon } from "leaflet";
import { toast } from "sonner";
import { 
  MapPin, 
  Plus, 
  Building2, 
  Phone, 
  Mail, 
  Search,
  Filter,
  X,
  Trash2,
  Edit,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  RotateCcw
} from "lucide-react";
import "leaflet/dist/leaflet.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "pk.eyJ1IjoicnVkeWJyYXRod2FpdGUiLCJhIjoiY21sYjBoa3lhMGwwOTNocG9rcGY5aDB3aiJ9.ku6PEpWekH8XiTYXwNDCdw";

// Custom marker icon
const createCustomIcon = (color = "#22D3EE") => {
  return divIcon({
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    "></div>`,
    className: "custom-marker",
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Custom cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = "small";
  let bgColor = "#22D3EE";
  
  if (count >= 100) {
    size = "large";
    bgColor = "#EF4444";
  } else if (count >= 20) {
    size = "medium";
    bgColor = "#F59E0B";
  }
  
  const sizes = {
    small: { width: 36, height: 36, fontSize: 12 },
    medium: { width: 44, height: 44, fontSize: 14 },
    large: { width: 52, height: 52, fontSize: 16 }
  };
  
  const s = sizes[size];
  
  return divIcon({
    html: `<div style="
      background-color: ${bgColor};
      width: ${s.width}px;
      height: ${s.height}px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 15px rgba(0,0,0,0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: ${s.fontSize}px;
    ">${count}</div>`,
    className: "custom-cluster-icon",
    iconSize: [s.width, s.height],
    iconAnchor: [s.width / 2, s.height / 2],
  });
};

const SPECIALTIES = [
  "Physical Therapy",
  "Occupational Therapy",
  "Speech Therapy",
  "Nursing",
  "Radiology",
  "Emergency Medicine",
  "Internal Medicine",
  "Pediatrics",
  "Cardiology",
  "Orthopedics",
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const ITEMS_PER_PAGE = 20;

function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 10);
    }
  }, [center, zoom, map]);
  return null;
}

export default function MapView() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterSpecialty, setFilterSpecialty] = useState("");
  const [filterOrganization, setFilterOrganization] = useState("");
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  // Map and selection
  const [selectedSite, setSelectedSite] = useState(null);
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]);
  const [mapZoom, setMapZoom] = useState(4);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: "", address: "", city: "", state: "", zip_code: "",
    specialty: "", contact_email: "", contact_phone: "", description: "",
  });
  const [editFormData, setEditFormData] = useState({
    name: "", address: "", city: "", state: "", zip_code: "",
    specialty: "", contact_email: "", contact_phone: "", description: "",
  });
  const [geocoding, setGeocoding] = useState(false);
  const [editGeocoding, setEditGeocoding] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sitesRes, orgsRes] = await Promise.all([
        api.get("/clinical-sites"),
        api.get("/admin/organizations").catch(() => ({ data: [] }))
      ]);
      setSites(sitesRes.data);
      setOrganizations(orgsRes.data);
      
      // Set default filter to most common state if lots of sites
      if (sitesRes.data.length > 50) {
        const stateCounts = {};
        sitesRes.data.forEach(s => {
          stateCounts[s.state] = (stateCounts[s.state] || 0) + 1;
        });
        const mostCommonState = Object.entries(stateCounts)
          .sort((a, b) => b[1] - a[1])[0]?.[0];
        if (mostCommonState) {
          setFilterState(mostCommonState);
        }
      }
    } catch (error) {
      toast.error("Failed to load clinical sites");
    } finally {
      setLoading(false);
    }
  };

  // Filter sites based on all criteria
  const filteredSites = useMemo(() => {
    return sites.filter((site) => {
      const matchesSearch = !searchTerm || 
        site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.organization?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesState = !filterState || site.state === filterState;
      const matchesSpecialty = !filterSpecialty || site.specialty === filterSpecialty;
      const matchesOrg = !filterOrganization || site.organization_id === filterOrganization;
      const matchesAvailable = !showAvailableOnly || (site.available_slots > 0);
      return matchesSearch && matchesState && matchesSpecialty && matchesOrg && matchesAvailable;
    });
  }, [sites, searchTerm, filterState, filterSpecialty, filterOrganization, showAvailableOnly]);

  // Paginated list
  const paginatedSites = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredSites.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSites, currentPage]);

  const totalPages = Math.ceil(filteredSites.length / ITEMS_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterState, filterSpecialty, filterOrganization, showAvailableOnly]);

  const activeFilterCount = [filterState, filterSpecialty, filterOrganization, showAvailableOnly].filter(Boolean).length;

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterState("");
    setFilterSpecialty("");
    setFilterOrganization("");
    setShowAvailableOnly(false);
    setFilterDrawerOpen(false);
  };

  const handleSiteClick = useCallback((site) => {
    setSelectedSite(site);
    setMapCenter([site.latitude, site.longitude]);
    setMapZoom(12);
  }, []);

  const handleCreateSite = async (e) => {
    e.preventDefault();
    setGeocoding(true);
    
    try {
      const addressQuery = `${formData.address}, ${formData.city}, ${formData.state}, ${formData.zip_code}, USA`;
      const geocodeResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&limit=1`
      );
      const geocodeData = await geocodeResponse.json();
      
      let latitude, longitude;
      if (geocodeData && geocodeData.length > 0) {
        latitude = parseFloat(geocodeData[0].lat);
        longitude = parseFloat(geocodeData[0].lon);
      } else {
        const stateCenters = {
          "AL": [32.806671, -86.791130], "AK": [61.370716, -152.404419], "AZ": [33.729759, -111.431221],
          "AR": [34.969704, -92.373123], "CA": [36.116203, -119.681564], "CO": [39.059811, -105.311104],
          "CT": [41.597782, -72.755371], "DE": [39.318523, -75.507141], "FL": [27.766279, -81.686783],
          "GA": [33.040619, -83.643074], "HI": [21.094318, -157.498337], "ID": [44.240459, -114.478828],
          "IL": [40.349457, -88.986137], "IN": [39.849426, -86.258278], "IA": [42.011539, -93.210526],
          "KS": [38.526600, -96.726486], "KY": [37.668140, -84.670067], "LA": [31.169546, -91.867805],
          "ME": [44.693947, -69.381927], "MD": [39.063946, -76.802101], "MA": [42.230171, -71.530106],
          "MI": [43.326618, -84.536095], "MN": [45.694454, -93.900192], "MS": [32.741646, -89.678696],
          "MO": [38.456085, -92.288368], "MT": [46.921925, -110.454353], "NE": [41.125370, -98.268082],
          "NV": [38.313515, -117.055374], "NH": [43.452492, -71.563896], "NJ": [40.298904, -74.521011],
          "NM": [34.840515, -106.248482], "NY": [42.165726, -74.948051], "NC": [35.630066, -79.806419],
          "ND": [47.528912, -99.784012], "OH": [40.388783, -82.764915], "OK": [35.565342, -96.928917],
          "OR": [44.572021, -122.070938], "PA": [40.590752, -77.209755], "RI": [41.680893, -71.511780],
          "SC": [33.856892, -80.945007], "SD": [44.299782, -99.438828], "TN": [35.747845, -86.692345],
          "TX": [31.054487, -97.563461], "UT": [40.150032, -111.862434], "VT": [44.045876, -72.710686],
          "VA": [37.769337, -78.169968], "WA": [47.400902, -121.490494], "WV": [38.491226, -80.954453],
          "WI": [44.268543, -89.616508], "WY": [42.755966, -107.302490]
        };
        const coords = stateCenters[formData.state] || [39.8283, -98.5795];
        latitude = coords[0];
        longitude = coords[1];
        toast.info("Using approximate location.");
      }
      
      const response = await api.post("/clinical-sites", { ...formData, latitude, longitude });
      setSites([...sites, response.data]);
      setDialogOpen(false);
      setFormData({
        name: "", address: "", city: "", state: "", zip_code: "",
        specialty: "", contact_email: "", contact_phone: "", description: "",
      });
      toast.success("Clinical site created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create clinical site");
    } finally {
      setGeocoding(false);
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!confirm("Delete this clinical site and all its rotations?")) return;
    try {
      await api.delete(`/clinical-sites/${siteId}`);
      setSites(sites.filter(s => s.id !== siteId));
      if (selectedSite?.id === siteId) setSelectedSite(null);
      toast.success("Clinical site deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete clinical site");
    }
  };

  const handleOpenEditDialog = (site) => {
    setEditingSite(site);
    setEditFormData({
      name: site.name || "", address: site.address || "", city: site.city || "",
      state: site.state || "", zip_code: site.zip_code || "", specialty: site.specialty || "",
      contact_email: site.contact_email || "", contact_phone: site.contact_phone || "",
      description: site.description || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateSite = async (e) => {
    e.preventDefault();
    if (!editingSite) return;
    
    setEditGeocoding(true);
    try {
      let latitude = editingSite.latitude;
      let longitude = editingSite.longitude;
      
      const addressChanged = 
        editFormData.address !== editingSite.address ||
        editFormData.city !== editingSite.city ||
        editFormData.state !== editingSite.state;
      
      if (addressChanged && editFormData.address && editFormData.city && editFormData.state) {
        const addressQuery = `${editFormData.address}, ${editFormData.city}, ${editFormData.state}, ${editFormData.zip_code}, USA`;
        const geocodeResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&limit=1`
        );
        const geocodeData = await geocodeResponse.json();
        
        if (geocodeData && geocodeData.length > 0) {
          latitude = parseFloat(geocodeData[0].lat);
          longitude = parseFloat(geocodeData[0].lon);
        }
      }
      
      const response = await api.put(`/clinical-sites/${editingSite.id}`, {
        ...editFormData, latitude, longitude,
      });
      
      setSites(sites.map(s => s.id === editingSite.id ? response.data : s));
      if (selectedSite?.id === editingSite.id) setSelectedSite(response.data);
      setEditDialogOpen(false);
      setEditingSite(null);
      toast.success("Clinical site updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update clinical site");
    } finally {
      setEditGeocoding(false);
    }
  };

  const canEditSite = (site) => {
    if (user?.role === "admin") return true;
    if (user?.role === "clinical_site" && user?.clinical_site_id === site?.id) return true;
    return false;
  };

  const canManageSites = user?.role === "admin" || user?.role === "clinical_site";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="map-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Clinical Sites Map</h1>
          <p className="text-slate-400 mt-1">
            {filteredSites.length} of {sites.length} clinical sites
            {filterState && ` in ${filterState}`}
          </p>
        </div>
        {canManageSites && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-cyan-400 text-black hover:bg-cyan-300 font-semibold shadow-[0_0_15px_rgba(34,211,238,0.3)]"
                data-testid="add-site-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Clinical Site
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#151A23] border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-slate-100">Add New Clinical Site</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateSite} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-300">Site Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="General Hospital"
                      data-testid="site-name-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300">Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="123 Medical Center Dr"
                      data-testid="site-address-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="New York"
                      data-testid="site-city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">State</Label>
                    <SearchableSelect
                      options={US_STATES.map(state => ({ value: state, label: state }))}
                      value={formData.state}
                      onValueChange={(value) => setFormData({ ...formData, state: value })}
                      placeholder="Select state..."
                      searchPlaceholder="Type state..."
                      emptyText="No matching states."
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">ZIP Code</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="10001"
                      data-testid="site-zip-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Specialty</Label>
                    <Select onValueChange={(value) => setFormData({ ...formData, specialty: value })}>
                      <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="site-specialty-select">
                        <SelectValue placeholder="Select specialty" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#151A23] border-slate-700">
                        {SPECIALTIES.map((spec) => (
                          <SelectItem key={spec} value={spec} className="text-slate-200 focus:bg-slate-800">
                            {spec}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Contact Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="contact@hospital.com"
                      data-testid="site-email-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Contact Phone</Label>
                    <PhoneInput
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      data-testid="site-phone-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300">Description (Optional)</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="Brief description..."
                      data-testid="site-description-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-cyan-400 text-black hover:bg-cyan-300" disabled={geocoding} data-testid="submit-site-btn">
                    {geocoding ? "Locating..." : "Create Site"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            placeholder="Search by name, city, address, or organization..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700 text-slate-200"
            data-testid="search-sites-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setFilterDrawerOpen(true)}
          className={`border-slate-700 text-slate-300 hover:bg-slate-800 ${activeFilterCount > 0 ? 'border-cyan-500' : ''}`}
          data-testid="open-filters-btn"
        >
          <SlidersHorizontal className="w-4 h-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <Badge className="ml-2 bg-cyan-500 text-black">{activeFilterCount}</Badge>
          )}
        </Button>
        {(searchTerm || activeFilterCount > 0) && (
          <Button 
            variant="ghost" 
            onClick={clearAllFilters}
            className="text-slate-400 hover:text-white"
            data-testid="clear-all-filters-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
        )}
      </div>

      {/* Filter Drawer/Modal */}
      <Dialog open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Filter className="w-5 h-5 text-cyan-400" />
              Filter Clinical Sites
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            {/* State Filter */}
            <div>
              <Label className="text-slate-300 mb-2 block">State</Label>
              <SearchableSelect
                options={[
                  { value: "all", label: "All States" },
                  ...US_STATES.map(state => ({ value: state, label: state }))
                ]}
                value={filterState || "all"}
                onValueChange={(v) => setFilterState(v === "all" ? "" : v)}
                placeholder="Select state..."
                searchPlaceholder="Type state name..."
                emptyText="No matching states."
              />
            </div>

            {/* Specialty Filter */}
            <div>
              <Label className="text-slate-300 mb-2 block">Specialty</Label>
              <Select value={filterSpecialty || "all"} onValueChange={(v) => setFilterSpecialty(v === "all" ? "" : v)}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="filter-specialty-select">
                  <SelectValue placeholder="All Specialties" />
                </SelectTrigger>
                <SelectContent className="bg-[#151A23] border-slate-700">
                  <SelectItem value="all" className="text-slate-200 focus:bg-slate-800">All Specialties</SelectItem>
                  {SPECIALTIES.map((spec) => (
                    <SelectItem key={spec} value={spec} className="text-slate-200 focus:bg-slate-800">
                      {spec}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Organization Filter */}
            {organizations.length > 0 && (
              <div>
                <Label className="text-slate-300 mb-2 block">Organization</Label>
                <SearchableSelect
                  options={[
                    { value: "all", label: "All Organizations" },
                    ...organizations.map(org => ({ value: org.id, label: org.name }))
                  ]}
                  value={filterOrganization || "all"}
                  onValueChange={(v) => setFilterOrganization(v === "all" ? "" : v)}
                  placeholder="Select organization..."
                  searchPlaceholder="Type organization name..."
                  emptyText="No matching organizations."
                />
              </div>
            )}

            {/* Available Slots Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Show only with available slots</Label>
              <button
                type="button"
                onClick={() => setShowAvailableOnly(!showAvailableOnly)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  showAvailableOnly ? 'bg-cyan-500' : 'bg-slate-700'
                }`}
                data-testid="filter-available-toggle"
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  showAvailableOnly ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t border-slate-700">
              <Button
                variant="ghost"
                onClick={clearAllFilters}
                className="text-slate-400"
              >
                Clear All
              </Button>
              <Button
                onClick={() => setFilterDrawerOpen(false)}
                className="bg-cyan-400 text-black hover:bg-cyan-300"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map and List Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map with Clustering */}
        <Card className="lg:col-span-2 bg-[#151A23] border-slate-700 overflow-hidden" data-testid="map-container">
          <div className="w-full h-[600px] rounded-xl overflow-hidden relative z-0">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: "100%", width: "100%" }}
              className="z-0"
            >
              <TileLayer
                url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/256/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
                attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a>'
              />
              <MapController center={mapCenter} zoom={mapZoom} />
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterCustomIcon}
                maxClusterRadius={60}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
              >
                {filteredSites.map((site) => (
                  <Marker
                    key={site.id}
                    position={[site.latitude, site.longitude]}
                    icon={createCustomIcon(selectedSite?.id === site.id ? "#10B981" : "#22D3EE")}
                    eventHandlers={{
                      click: () => handleSiteClick(site),
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[220px]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">
                            {site.short_id || 'CS----'}
                          </span>
                        </div>
                        <h3 className="font-semibold text-slate-100">{site.name}</h3>
                        <p className="text-slate-400 text-sm mt-1">{site.address}</p>
                        <p className="text-slate-400 text-sm">{site.city}, {site.state} {site.zip_code}</p>
                        {site.organization && (
                          <p className="text-slate-500 text-xs mt-1">{site.organization}</p>
                        )}
                        <Badge className="mt-2 bg-cyan-400/10 text-cyan-400 border-cyan-400/20">
                          {site.specialty}
                        </Badge>
                        <p className="text-slate-300 text-sm mt-2">
                          {site.available_slots} of {site.total_slots} slots available
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        </Card>

        {/* Sites List with Pagination */}
        <Card className="bg-[#151A23] border-slate-700" data-testid="sites-list">
          <CardHeader className="pb-3">
            <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-cyan-400" />
              Sites ({filteredSites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="max-h-[440px] overflow-y-auto space-y-2 pr-1">
              {paginatedSites.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No sites found</p>
                  <p className="text-slate-500 text-sm mt-1">Try adjusting your filters</p>
                </div>
              ) : (
                paginatedSites.map((site) => (
                  <div
                    key={site.id}
                    onClick={() => handleSiteClick(site)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedSite?.id === site.id
                        ? "bg-cyan-400/10 border border-cyan-400/50"
                        : "bg-slate-800/30 hover:bg-slate-800/50 border border-transparent"
                    }`}
                    data-testid={`site-item-${site.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-mono text-[10px] bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">
                            {site.short_id || 'CS----'}
                          </span>
                        </div>
                        <h3 className="font-medium text-slate-200 text-sm truncate">{site.name}</h3>
                        <p className="text-slate-500 text-xs mt-0.5 truncate" title={site.address}>
                          {site.address}
                        </p>
                        <p className="text-slate-500 text-xs truncate">
                          {site.city}, {site.state} {site.zip_code}
                        </p>
                      </div>
                      <Badge className="bg-slate-700 text-slate-300 text-xs whitespace-nowrap flex-shrink-0">
                        {site.available_slots}/{site.total_slots}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className="bg-cyan-400/10 text-cyan-400 border-cyan-400/20 text-xs">
                        {site.specialty}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="text-slate-400 disabled:opacity-50"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-slate-400 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="text-slate-400 disabled:opacity-50"
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Selected Site Details */}
      {selectedSite && (
        <Card className="bg-[#151A23] border-slate-700 animate-fade-in" data-testid="site-details">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-slate-100">{selectedSite.name}</CardTitle>
              {selectedSite.organization && (
                <p className="text-slate-500 text-sm mt-1">{selectedSite.organization}</p>
              )}
            </div>
            <div className="flex gap-2">
              {canEditSite(selectedSite) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenEditDialog(selectedSite)}
                  className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10"
                  data-testid="edit-site-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
              {canManageSites && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteSite(selectedSite.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  data-testid="delete-site-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedSite(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-slate-300">
                  <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <div>
                    <p>{selectedSite.address}</p>
                    <p className="text-slate-500">{selectedSite.city}, {selectedSite.state} {selectedSite.zip_code}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="truncate">{selectedSite.contact_email}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <Phone className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span>{selectedSite.contact_phone}</span>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-slate-500 text-sm">Specialty</p>
                  <Badge className="mt-1 bg-cyan-400/10 text-cyan-400 border-cyan-400/20">
                    {selectedSite.specialty}
                  </Badge>
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Rotations</p>
                  <p className="text-2xl font-bold text-slate-100">{selectedSite.total_rotations}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Available Slots</p>
                  <p className="text-2xl font-bold text-cyan-400">{selectedSite.available_slots}</p>
                  <p className="text-slate-500 text-xs">of {selectedSite.total_slots} total</p>
                </div>
              </div>
              <div>
                {selectedSite.description && (
                  <div>
                    <p className="text-slate-500 text-sm">Description</p>
                    <p className="text-slate-300 mt-1">{selectedSite.description}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Edit Clinical Site</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateSite} className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Site Name</Label>
              <Input
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                data-testid="edit-site-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-slate-300">Address</Label>
                <Input
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                />
              </div>
              <div>
                <Label className="text-slate-300">City</Label>
                <Input
                  value={editFormData.city}
                  onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                />
              </div>
              <div>
                <Label className="text-slate-300">State</Label>
                <SearchableSelect
                  options={US_STATES.map(s => ({ value: s, label: s }))}
                  value={editFormData.state}
                  onValueChange={(v) => setEditFormData({ ...editFormData, state: v })}
                  placeholder="Select state..."
                  searchPlaceholder="Type state..."
                  emptyText="No matching states."
                />
              </div>
              <div>
                <Label className="text-slate-300">ZIP</Label>
                <Input
                  value={editFormData.zip_code}
                  onChange={(e) => setEditFormData({ ...editFormData, zip_code: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                />
              </div>
              <div>
                <Label className="text-slate-300">Specialty</Label>
                <Select value={editFormData.specialty} onValueChange={(v) => setEditFormData({ ...editFormData, specialty: v })}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-slate-700">
                    {SPECIALTIES.map((s) => (
                      <SelectItem key={s} value={s} className="text-slate-200">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={editFormData.contact_email}
                  onChange={(e) => setEditFormData({ ...editFormData, contact_email: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone</Label>
                <PhoneInput
                  value={editFormData.contact_phone}
                  onChange={(e) => setEditFormData({ ...editFormData, contact_phone: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                  data-testid="edit-site-phone-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-slate-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-cyan-400 text-black hover:bg-cyan-300" disabled={editGeocoding}>
                {editGeocoding ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
